import { useCallback, useEffect, useState } from "react";

const KEY = "pm_shortlist";

// A tiny cross-component shortlist backed by localStorage. Every hook instance
// subscribes to a shared in-memory set + a storage event, so toggling on a card
// updates the floating bar and the saved-view at once.
const listeners = new Set();

function read() {
  try {
    return new Set(JSON.parse(localStorage.getItem(KEY) || "[]"));
  } catch {
    return new Set();
  }
}

let current = typeof window !== "undefined" ? read() : new Set();

function write(next) {
  current = next;
  localStorage.setItem(KEY, JSON.stringify(Array.from(next)));
  listeners.forEach((fn) => fn(next));
}

export function useShortlist() {
  const [set, setSet] = useState(current);

  useEffect(() => {
    const fn = (next) => setSet(new Set(next));
    listeners.add(fn);
    // Sync across tabs.
    const onStorage = (e) => {
      if (e.key === KEY) {
        current = read();
        fn(current);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      listeners.delete(fn);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const toggle = useCallback((token) => {
    const next = new Set(current);
    if (next.has(token)) next.delete(token);
    else next.add(token);
    write(next);
  }, []);

  const clear = useCallback(() => write(new Set()), []);
  const has = useCallback((token) => set.has(token), [set]);

  return { tokens: Array.from(set), count: set.size, has, toggle, clear };
}
